import React, { useState, useMemo, useRef } from 'react'
import styled from 'styled-components'
import IconButton from '@mui/material/IconButton'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined'
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined'
import AddIcon from '@mui/icons-material/Add'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import CircularProgress from '@mui/material/CircularProgress'
import { usePatientContext } from '@/v2/contexts/PatientContext'
import { useContracts } from '@/v2/hooks/documents/useContracts'
import { useIntakeForm } from '@/v2/hooks/documents/useIntakeForm'
import {
  useConsentForms,
  useConsentForm,
  useResendConsentForm,
} from '@/v2/hooks/documents/useConsentForm'
import useFolders from '@/v2/hooks/useFolders'
import useArchives from '@/v2/hooks/useArchives'
import useModals from '@/v2/hooks/useModals'
import { useSnackbarContext } from '@/v2/contexts/SnackbarContext'
import { useSendIntakeForm } from '@/v2/hooks/documents/useIntakeForm'
import {
  useArchiveContract,
  useSendContract,
} from '@/v2/hooks/documents/useContracts'
import { getContractStatus } from '@/v2/utils/helpers'
import { getDateTimeWithoutTimezone, safeFormat } from '@/v2/utils/convert'
import {
  INTAKE_FORM_STATUS,
  DATE_FORMAT,
  CONSENT_STATUS,
  CONTRACT_TREATMENT_TYPE_LABELS,
} from '@/v2/constants'

const Wrapper = styled.div`
  padding: 0;
  background: #f8f9fa;
  min-height: 100%;
  overflow-x: hidden;
`

const TabsContainer = styled.div`
  background: #fff;
  padding: 12px 16px;
  border-bottom: 1px solid #eee;
  position: sticky;
  top: 0;
  z-index: 10;
`

const TabsList = styled.div`
  display: flex;
  gap: 8px;
`

const Tab = styled.button`
  flex: 1;
  background: ${({ active }) => (active ? '#1976d2' : '#fff')};
  color: ${({ active }) => (active ? '#fff' : '#666')};
  border: ${({ active }) => (active ? 'none' : '1px solid #e0e0e0')};
  padding: 12px 16px;
  border-radius: 10px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: ${({ active }) =>
    active ? '0 2px 8px rgba(25, 118, 210, 0.25)' : 'none'};

  &:active {
    transform: scale(0.97);
  }
`

const Content = styled.div`
  padding: 16px;
  padding-bottom: 80px;
`

const SectionTitle = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #999;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 12px;
  padding: 0 4px;
`

const DocumentCard = styled.div`
  background: #fff;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  display: flex;
  align-items: flex-start;
  gap: 14px;
`

const DocumentIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: ${({ color }) => color || '#e3f2fd'};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  svg {
    font-size: 24px;
    color: ${({ iconColor }) => iconColor || '#1976d2'};
  }
`

const DocumentInfo = styled.div`
  flex: 1;
  min-width: 0;
`

const DocumentTitle = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: #333;
  margin-bottom: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const DocumentMeta = styled.div`
  font-size: 13px;
  color: #888;
  margin-bottom: 8px;
`

const StatusBadge = styled.span`
  display: inline-block;
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  background: ${({ color }) => {
    switch (color) {
      case 'green':
        return '#e8f5e9'
      case 'yellow':
        return '#fff8e1'
      case 'red':
        return '#ffebee'
      case 'gray':
        return '#f5f5f5'
      default:
        return '#e3f2fd'
    }
  }};
  color: ${({ color }) => {
    switch (color) {
      case 'green':
        return '#2e7d32'
      case 'yellow':
        return '#f57c00'
      case 'red':
        return '#c62828'
      case 'gray':
        return '#757575'
      default:
        return '#1565c0'
    }
  }};
`

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 24px;
  text-align: center;
`

const EmptyIcon = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: #f0f4f8;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;

  svg {
    font-size: 40px;
    color: #b0bec5;
  }
`

const EmptyTitle = styled.div`
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin-bottom: 8px;
`

const EmptyText = styled.div`
  font-size: 14px;
  color: #888;
  line-height: 1.5;
  max-width: 280px;
`

const FAB = styled.button`
  position: fixed;
  bottom: 100px;
  right: 20px;
  width: 56px;
  height: 56px;
  border-radius: 16px;
  background: ${({ disabled }) => (disabled ? '#90caf9' : '#1976d2')};
  color: #fff;
  border: none;
  box-shadow: 0 4px 12px rgba(25, 118, 210, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  z-index: 100;
  transition: all 0.2s ease;

  &:active {
    transform: ${({ disabled }) => (disabled ? 'none' : 'scale(0.95)')};
  }

  svg {
    font-size: 28px;
  }
`

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px;
  gap: 16px;
  color: #666;
`

const UploadCard = styled.div`
  background: #fff;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
`

const UploadHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
`

const UploadActions = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #f0f0f0;
`

const ActionButton = styled.button`
  flex: 1;
  padding: 10px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 1px solid #e0e0e0;
  background: #fff;
  color: #666;

  &:active {
    transform: scale(0.97);
  }

  &.primary {
    background: #1976d2;
    color: #fff;
    border: none;
  }

  &.danger {
    color: #d32f2f;
    border-color: #ffcdd2;
  }
`

const getFilesFromFolders = folders => {
  const files = []
  folders.forEach(folder => {
    if (folder.folders && folder.folders.length > 0) {
      const foldersFiles = getFilesFromFolders(folder.folders)
      files.push(...foldersFiles)
    }
    if (folder.archives && folder.archives.length > 0) {
      folder.archives.forEach(archive => {
        files.push(archive)
      })
    }
  })
  return files
}

const DocumentsMobile = () => {
  const { patient } = usePatientContext()
  const [tab, setTab] = useState('contracts')
  const { goTo } = useModals()
  const { onOpenSnackbar } = useSnackbarContext()
  const inputRef = useRef(null)
  const [menuAnchor, setMenuAnchor] = useState(null)
  const [selectedItem, setSelectedItem] = useState(null)
  const [addMenuAnchor, setAddMenuAnchor] = useState(null)

  const { intakeForm, isLoading: isLoadingForms } = useIntakeForm(patient?.id)
  const { contracts, isLoading: isLoadingContracts } = useContracts(patient?.id)
  const { consentForms } = useConsentForms()
  const {
    consentForms: patientConsentForms,
    isLoading: isLoadingConsentForms,
  } = useConsentForm(patient?.id)
  const { resendConsentForm } = useResendConsentForm(patient?.id)

  const { data: foldersData, isLoading: isLoadingFolders } = useFolders({
    patientId: patient?.id,
  })

  const {
    createArchive,
    deleteArchive,
    isLoadingCreate,
    isLoadingDelete,
  } = useArchives({
    patientId: patient?.id,
    folderId: foldersData?.id,
  })

  const archives = useMemo(() => {
    if (!foldersData) return []
    const files = [...foldersData.archives]
    if (foldersData.folders && foldersData.folders.length > 0) {
      const foldersFiles = getFilesFromFolders(foldersData.folders)
      files.push(...foldersFiles)
    }
    return files
  }, [foldersData])

  const hasActiveContract = useMemo(() => {
    if (!contracts) return false
    return contracts.find(contract => contract.active === true)
  }, [contracts])

  const handleMenuOpen = (event, item, type) => {
    event.stopPropagation()
    setMenuAnchor(event.currentTarget)
    setSelectedItem({ ...item, type })
  }

  const handleMenuClose = () => {
    setMenuAnchor(null)
    setSelectedItem(null)
  }

  const handleUploadClick = () => {
    inputRef.current?.click()
  }

  const handleFileChange = e => {
    if (!e.target.files) return
    const file = e.target.files[0]
    if (!file) return

    const formData = new FormData()
    formData.append('archive[file]', file)
    formData.append(
      'archive[name]',
      file?.name
        .split('.')
        .slice(0, -1)
        .join('.')
    )

    if (foldersData) {
      createArchive(formData)
    }
  }

  const handleAddMenuOpen = event => {
    setAddMenuAnchor(event.currentTarget)
  }

  const handleAddMenuClose = () => {
    setAddMenuAnchor(null)
  }

  const handleCreateContract = () => {
    handleAddMenuClose()
    goTo(`/v2/create-contract/${patient?.id}`, {
      initialValues: { email: patient?.email },
    })
  }

  const handleCreateConsentForm = () => {
    handleAddMenuClose()
    // The confirmation modal picks the form itself; this only opens it.
    if (consentForms?.length > 0) {
      goTo('/v2/consent-form-confirmation', { patientId: patient?.id })
    }
  }

  if (!patient?.id) return null

  const isLoading =
    isLoadingContracts ||
    isLoadingForms ||
    isLoadingFolders ||
    isLoadingConsentForms

  if (isLoading) {
    return (
      <Wrapper>
        <LoadingContainer>
          <CircularProgress size={32} />
          <span>Loading documents...</span>
        </LoadingContainer>
      </Wrapper>
    )
  }

  const renderContractsTab = () => {
    const hasDocuments =
      intakeForm ||
      (contracts && contracts.length > 0) ||
      (patientConsentForms && patientConsentForms.length > 0)

    if (!hasDocuments) {
      return (
        <EmptyState>
          <EmptyIcon>
            <DescriptionOutlinedIcon />
          </EmptyIcon>
          <EmptyTitle>No documents yet</EmptyTitle>
          <EmptyText>
            Create contracts and forms for this patient to get started
          </EmptyText>
        </EmptyState>
      )
    }

    const intakeStatus = intakeForm
      ? INTAKE_FORM_STATUS[intakeForm?.status]
      : null
    const hasConsentForms =
      patientConsentForms && patientConsentForms.length > 0
    const hasContracts = contracts && contracts.length > 0

    return (
      <>
        {intakeForm && (
          <>
            <SectionTitle>Health History</SectionTitle>
            <IntakeFormCard
              intakeForm={intakeForm}
              status={intakeStatus}
              onMenuOpen={handleMenuOpen}
            />
          </>
        )}

        {hasConsentForms && (
          <>
            <SectionTitle style={{ marginTop: intakeForm ? 24 : 0 }}>
              Consent Forms ({patientConsentForms.length})
            </SectionTitle>
            {patientConsentForms.map(form => (
              <ConsentFormCard
                key={form.id}
                consentForm={form}
                goTo={goTo}
                onOpenSnackbar={onOpenSnackbar}
                resendConsentForm={resendConsentForm}
              />
            ))}
          </>
        )}

        {hasContracts && (
          <>
            <SectionTitle
              style={{ marginTop: intakeForm || hasConsentForms ? 24 : 0 }}
            >
              Contracts ({contracts.length})
            </SectionTitle>
            {contracts.map(contract => (
              <ContractCard
                key={contract.id}
                contract={contract}
                onMenuOpen={handleMenuOpen}
              />
            ))}
          </>
        )}
      </>
    )
  }

  const renderUploadsTab = () => {
    if (!archives || archives.length === 0) {
      return (
        <EmptyState>
          <EmptyIcon>
            <CloudUploadOutlinedIcon />
          </EmptyIcon>
          <EmptyTitle>No uploads yet</EmptyTitle>
          <EmptyText>
            Tap the + button to upload documents for this patient
          </EmptyText>
        </EmptyState>
      )
    }

    return (
      <>
        <SectionTitle>Uploaded Files ({archives.length})</SectionTitle>
        {archives.map(upload => (
          <UploadCardComponent
            key={upload.id}
            upload={upload}
            onDelete={() => {
              if (confirm('Are you sure you want to delete this document?')) {
                deleteArchive({ id: upload.id })
              }
            }}
          />
        ))}
      </>
    )
  }

  return (
    <Wrapper>
      <TabsContainer>
        <TabsList>
          <Tab active={tab === 'contracts'} onClick={() => setTab('contracts')}>
            Forms & Contracts
          </Tab>
          <Tab active={tab === 'uploads'} onClick={() => setTab('uploads')}>
            Uploads
          </Tab>
        </TabsList>
      </TabsContainer>

      <Content>
        {tab === 'contracts' ? renderContractsTab() : renderUploadsTab()}
      </Content>

      <FAB
        onClick={tab === 'uploads' ? handleUploadClick : handleAddMenuOpen}
        disabled={isLoadingCreate}
      >
        {isLoadingCreate ? (
          <CircularProgress size={24} color="inherit" />
        ) : (
          <AddIcon />
        )}
      </FAB>

      <Menu
        anchorEl={addMenuAnchor}
        open={Boolean(addMenuAnchor)}
        onClose={handleAddMenuClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {!hasActiveContract && (
          <MenuItem onClick={handleCreateContract}>Create contract</MenuItem>
        )}
        {consentForms?.length > 0 && (
          <MenuItem onClick={handleCreateConsentForm}>
            Create consent form
          </MenuItem>
        )}
      </Menu>

      <input
        type="file"
        accept="image/png, image/jpeg, application/pdf, .doc, .docx, .xls, .xlsx, .zip, .rar"
        ref={inputRef}
        onChange={handleFileChange}
        style={{ display: 'none' }}
      />

      <DocumentMenu
        anchor={menuAnchor}
        item={selectedItem}
        onClose={handleMenuClose}
        patientId={patient?.id}
        goTo={goTo}
        onOpenSnackbar={onOpenSnackbar}
      />
    </Wrapper>
  )
}

const IntakeFormCard = ({ intakeForm, status, onMenuOpen }) => {
  const createdAt = getDateTimeWithoutTimezone(intakeForm.createdAt)

  return (
    <DocumentCard>
      <DocumentIcon color="#e8f5e9" iconColor="#43a047">
        <DescriptionOutlinedIcon />
      </DocumentIcon>
      <DocumentInfo>
        <DocumentTitle>
          Health History #{intakeForm.identifierToken}
        </DocumentTitle>
        <DocumentMeta>{safeFormat(createdAt, DATE_FORMAT)}</DocumentMeta>
        {status && (
          <StatusBadge color={status.color}>{status.label}</StatusBadge>
        )}
      </DocumentInfo>
      <IconButton
        size="small"
        onClick={e => onMenuOpen(e, intakeForm, 'intake')}
      >
        <MoreVertIcon />
      </IconButton>
    </DocumentCard>
  )
}

const ContractCard = ({ contract, onMenuOpen }) => {
  const contractStatus = getContractStatus(contract)
  const createdAt = getDateTimeWithoutTimezone(contract.createdAt)

  const getStatusColor = () => {
    if (!contract.active) return 'gray'
    if (contract.signed) return 'green'
    if (contract.emailSent) return 'yellow'
    return 'blue'
  }

  return (
    <DocumentCard>
      <DocumentIcon
        color={contract.signed ? '#e8f5e9' : '#fff3e0'}
        iconColor={contract.signed ? '#43a047' : '#ff9800'}
      >
        <DescriptionOutlinedIcon />
      </DocumentIcon>
      <DocumentInfo>
        <DocumentTitle>Contract #{contract.identifierToken}</DocumentTitle>
        <DocumentMeta>{safeFormat(createdAt, DATE_FORMAT)}</DocumentMeta>
        <DocumentMeta>
          {CONTRACT_TREATMENT_TYPE_LABELS[contract.treatmentType]}
        </DocumentMeta>
        {contractStatus && (
          <StatusBadge color={getStatusColor()}>
            {contractStatus.label}
          </StatusBadge>
        )}
      </DocumentInfo>
      <IconButton
        size="small"
        onClick={e => onMenuOpen(e, contract, 'contract')}
      >
        <MoreVertIcon />
      </IconButton>
    </DocumentCard>
  )
}

const ConsentFormCard = ({
  consentForm,
  goTo,
  onOpenSnackbar,
  resendConsentForm,
}) => {
  const status = CONSENT_STATUS[consentForm.status]
  const createdAt = getDateTimeWithoutTimezone(consentForm.createdAt)
  const [menuAnchor, setMenuAnchor] = useState(null)

  const getStatusColor = () => {
    if (consentForm.status === 'signed') return 'green'
    if (consentForm.status === 'sent') return 'yellow'
    return 'blue'
  }

  const handleMenuOpen = event => {
    event.stopPropagation()
    setMenuAnchor(event.currentTarget)
  }

  const handleMenuClose = () => {
    setMenuAnchor(null)
  }

  const handleView = () => {
    goTo(`/v2/consent-form-details/${consentForm.consentForm.id}`, {
      signature: consentForm.patientSignature,
    })
    handleMenuClose()
  }

  const handleSend = async () => {
    try {
      const { error } = await resendConsentForm(consentForm.id)
      if (!error) {
        onOpenSnackbar('Consent form sent successfully')
      }
    } catch (error) {
      onOpenSnackbar('Failed to send consent form', 'error')
    }
    handleMenuClose()
  }

  const handleDownloadPdf = () => {
    window.open(`/consent_form_signatures/${consentForm.id}/pdf`, '_blank')
    handleMenuClose()
  }

  return (
    <>
      <DocumentCard>
        <DocumentIcon
          color={consentForm.status === 'signed' ? '#e8f5e9' : '#e3f2fd'}
          iconColor={consentForm.status === 'signed' ? '#43a047' : '#1976d2'}
        >
          <DescriptionOutlinedIcon />
        </DocumentIcon>
        <DocumentInfo>
          <DocumentTitle>
            Consent Form {consentForm.consentForm?.title?.toLowerCase()}
          </DocumentTitle>
          <DocumentMeta>{safeFormat(createdAt, DATE_FORMAT)}</DocumentMeta>
          {status && (
            <StatusBadge color={getStatusColor()}>{status.label}</StatusBadge>
          )}
        </DocumentInfo>
        <IconButton size="small" onClick={handleMenuOpen}>
          <MoreVertIcon />
        </IconButton>
      </DocumentCard>
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleView}>View</MenuItem>
        {consentForm.status === 'signed' ? (
          <MenuItem onClick={handleDownloadPdf}>Download PDF</MenuItem>
        ) : (
          <MenuItem onClick={handleSend}>Send for Signature</MenuItem>
        )}
      </Menu>
    </>
  )
}

const UploadCardComponent = ({ upload, onDelete }) => {
  const createdAt = getDateTimeWithoutTimezone(upload.createdAt)
  const fileNameWithExtension = upload.url?.split('/').pop() || ''

  const handleView = () => {
    window.open(upload.url, '_blank')
  }

  return (
    <UploadCard>
      <UploadHeader>
        <DocumentIcon color="#e3f2fd" iconColor="#1976d2">
          <DescriptionOutlinedIcon />
        </DocumentIcon>
        <DocumentInfo>
          <DocumentTitle>{upload.name}</DocumentTitle>
          <DocumentMeta>
            {fileNameWithExtension.replace(/%20/g, ' ')} &bull;{' '}
            {safeFormat(createdAt, DATE_FORMAT)}
          </DocumentMeta>
        </DocumentInfo>
      </UploadHeader>
      <UploadActions>
        <ActionButton className="primary" onClick={handleView}>
          View
        </ActionButton>
        <ActionButton className="danger" onClick={onDelete}>
          Delete
        </ActionButton>
      </UploadActions>
    </UploadCard>
  )
}

export const DocumentMenu = ({
  anchor,
  item,
  onClose,
  patientId,
  goTo,
  onOpenSnackbar,
}) => {
  const { sendIntakeForm } = useSendIntakeForm(item?.id)
  const { sendContract } = useSendContract(item?.id)
  const { archiveContract } = useArchiveContract(patientId, item?.id)

  if (!item) return null

  const handleViewIntake = () => {
    window.open(`/intake_forms/${item.id}`, '_blank')
    onClose()
  }

  const handleDownloadIntakePdf = () => {
    window.open(`/intake_forms/${item.id}/pdf`, '_blank')
    onClose()
  }

  const handleSendIntake = async () => {
    try {
      const { error } = await sendIntakeForm()
      if (!error) {
        onOpenSnackbar('Health history form sent successfully')
      }
    } catch (error) {
      onOpenSnackbar('Failed to send form', 'error')
    }
    onClose()
  }

  const handleViewContract = () => {
    goTo(`/v2/contract-details/${item.id}`, {
      initialValues: { contract: item, patientId },
    })
    onClose()
  }

  const handleSendContract = async () => {
    try {
      const { error } = await sendContract()
      if (!error) {
        onOpenSnackbar('Contract email sent successfully')
      }
    } catch (error) {
      onOpenSnackbar('Failed to send email', 'error')
    }
    onClose()
  }

  const handleDownloadPdf = () => {
    window.open(`/contracts/${item.id}/pdf`, '_blank')
    onClose()
  }

  const handleArchive = async () => {
    try {
      const { error } = await archiveContract()
      if (!error) {
        onOpenSnackbar('Contract archived successfully')
      }
    } catch (error) {
      onOpenSnackbar('Failed to archive contract', 'error')
    }
    onClose()
  }

  return (
    <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={onClose}>
      {item.type === 'intake' && (
        <>
          {item.status === 'completed' ? (
            <>
              <MenuItem onClick={handleViewIntake}>View</MenuItem>
              <MenuItem onClick={handleDownloadIntakePdf}>
                Download PDF
              </MenuItem>
            </>
          ) : (
            <MenuItem onClick={handleSendIntake}>Send for Signature</MenuItem>
          )}
        </>
      )}
      {item.type === 'contract' && (
        <>
          <MenuItem onClick={handleViewContract}>View Details</MenuItem>
          {item.active && (
            <>
              {item.signed ? (
                <MenuItem onClick={handleDownloadPdf}>Download PDF</MenuItem>
              ) : (
                <MenuItem onClick={handleSendContract}>
                  {item.emailSent ? 'Resend Email' : 'Send Email'}
                </MenuItem>
              )}
              <MenuItem onClick={handleArchive}>Archive</MenuItem>
            </>
          )}
        </>
      )}
    </Menu>
  )
}

export default DocumentsMobile
